"use client";

import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import styles from "../css/ViewAllAppointments.module.css";
import { getAppointments as fetchAppointmentsApi, updateAppointmentStatus as updateAppointmentStatusApi } from "../../../../api/clinic/bookingApi.js";
import Button from "../../../../components/Button.jsx";
import DatePicker from "../../../../components/DatePicker.jsx";
import { formatISOToDDMMYYYY } from "../utils/scheduleConsultUtils.js";

// Helper to safely extract YYYY-MM-DD from various date formats
const extractDateISO = (val) => {
  if (!val) return "";
  try {
    // If it's already a string in YYYY-MM-DD format
    const strVal = String(val);
    if (/^\d{4}-\d{2}-\d{2}$/.test(strVal)) return strVal;
    // Try parsing as Date
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    // Fallback: extract first 10 chars if it looks like ISO
    if (strVal.length >= 10 && strVal.includes('-')) {
      return strVal.slice(0, 10);
    }
  } catch (e) {
    console.error('[extractDateISO] Failed to parse:', val, e);
  }
  return "";
};

// Helper to extract HH:mm:ss from various time formats
const extractTimeHMS = (val) => {
  if (!val) return "";
  try {
    const strVal = String(val);
    // If it's an ISO datetime string (e.g., "1970-01-01T08:40:00.000Z")
    if (strVal.includes('T')) {
      const d = new Date(strVal);
      if (!isNaN(d.getTime())) {
        const h = String(d.getUTCHours()).padStart(2, '0');
        const m = String(d.getUTCMinutes()).padStart(2, '0');
        const s = String(d.getUTCSeconds()).padStart(2, '0');
        return `${h}:${m}:${s}`;
      }
    }
    // If it's already in HH:mm:ss or HH:mm format
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(strVal)) {
      const parts = strVal.split(':');
      const h = parts[0].padStart(2, '0');
      const m = parts[1].padStart(2, '0');
      const s = (parts[2] || '00').padStart(2, '0');
      return `${h}:${m}:${s}`;
    }
  } catch (e) {
    console.error('[extractTimeHMS] Failed to parse:', val, e);
  }
  return "";
};

// This component expects parent to provide full list via location state or localStorage fallback
export default function ViewAllAppointments() {

  // Read from localStorage (same key as Clinic.jsx) to show the full list
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 15;
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateRangeFilter, setDateRangeFilter] = useState("All");
  const [timeFilter, setTimeFilter] = useState("All");

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchAppointmentsApi();
        const mapped = (Array.isArray(data)
          ? data.map((a) => {
              let dateIso = extractDateISO(a.preferreddate);
              if (!dateIso || dateIso === "1970-01-01") {
                const ca = a.createdat || a.createdAt;
                if (ca) dateIso = extractDateISO(ca);
              }
              const timeStr = extractTimeHMS(a.preferredtime);
              const name = `${a.patient?.firstname || ""} ${a.patient?.surname || ""}`.trim();
              return {
                id: a.appointmentid ?? a.id ?? crypto.randomUUID(),
                patient: { name, phone: a.patient?.contactnumber || "", email: a.patient?.email || "" },
                serviceDetail: "",
                date: dateIso,
                time: timeStr,
                status: a.status || "Pending",
                createdAt: a.createdat || a.createdAt || "",
              };
            })
          : []);
        if (!cancelled) setAppointments(mapped);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load appointments");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const todayISO = new Date().toLocaleDateString("en-CA");
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    
    let filtered = appointments.filter((a) => {
      // Text search filter
      const matchesSearch = !q ||
        [a.patient?.name, a.serviceDetail, a.date, a.time]
          .filter(Boolean)
          .some((t) => String(t).toLowerCase().includes(q));
      
      if (!matchesSearch) return false;
      
      // Filter out past appointments UNLESS dateRangeFilter is set to show past appointments or specific date is selected
      const showPastAppointments = dateRangeFilter === "Yesterday" || dateRangeFilter === "LastWeek" || dateRangeFilter === "LastMonth" || dateRangeFilter === "AllPast" || selectedDate;
      
      if (!showPastAppointments) {
        if (a.date) {
          const appointmentDate = new Date(a.date + 'T00:00:00');
          const isPast = appointmentDate < new Date(todayISO + 'T00:00:00');
          
          // For cancelled appointments, they stay visible all day if scheduled for today, but are filtered out if past
          const isCancelled = (a.status || "").toLowerCase() === "cancelled";
          
          // Filter out past appointments (including cancelled ones)
          if (isPast) return false;
          
          // If appointment is today and not cancelled, check if time has passed
          // Cancelled appointments for today stay visible regardless of time
          if (a.date === todayISO && !isCancelled && a.time) {
            const [hours, minutes] = a.time.split(':').map(Number);
            const appointmentTimeInMinutes = (hours || 0) * 60 + (minutes || 0);
            if (appointmentTimeInMinutes < currentTimeInMinutes) return false;
          }
        }
      }
      
      return true;
    });
    
    // Date filtering
    if (selectedDate) {
      const selectedDateISO = selectedDate.toLocaleDateString("en-CA");
      filtered = filtered.filter(a => a.date === selectedDateISO);
    } else if (dateRangeFilter !== "All") {
      const today = new Date();
      const todayISO = today.toLocaleDateString("en-CA");
      
      switch (dateRangeFilter) {
        case "Today":
          filtered = filtered.filter(a => a.date === todayISO);
          break;
        case "Tomorrow":
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          const tomorrowISO = tomorrow.toLocaleDateString("en-CA");
          filtered = filtered.filter(a => a.date === tomorrowISO);
          break;
        case "ThisWeek":
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          filtered = filtered.filter(a => {
            if (!a.date) return false;
            const apptDate = new Date(a.date + 'T00:00:00');
            return apptDate >= weekStart && apptDate <= weekEnd;
          });
          break;
        case "NextWeek":
          const nextWeekStart = new Date(today);
          nextWeekStart.setDate(today.getDate() - today.getDay() + 7);
          const nextWeekEnd = new Date(nextWeekStart);
          nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
          filtered = filtered.filter(a => {
            if (!a.date) return false;
            const apptDate = new Date(a.date + 'T00:00:00');
            return apptDate >= nextWeekStart && apptDate <= nextWeekEnd;
          });
          break;
        case "ThisMonth":
          const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
          const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          filtered = filtered.filter(a => {
            if (!a.date) return false;
            const apptDate = new Date(a.date + 'T00:00:00');
            return apptDate >= monthStart && apptDate <= monthEnd;
          });
          break;
        case "Yesterday":
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          const yesterdayISO = yesterday.toLocaleDateString("en-CA");
          filtered = filtered.filter(a => a.date === yesterdayISO);
          break;
        case "LastWeek":
          const lastWeekStart = new Date(today);
          lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
          const lastWeekEnd = new Date(lastWeekStart);
          lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
          filtered = filtered.filter(a => {
            if (!a.date) return false;
            const apptDate = new Date(a.date + 'T00:00:00');
            return apptDate >= lastWeekStart && apptDate <= lastWeekEnd;
          });
          break;
        case "LastMonth":
          const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
          filtered = filtered.filter(a => {
            if (!a.date) return false;
            const apptDate = new Date(a.date + 'T00:00:00');
            return apptDate >= lastMonthStart && apptDate <= lastMonthEnd;
          });
          break;
        case "AllPast":
          filtered = filtered.filter(a => {
            if (!a.date) return false;
            const apptDate = new Date(a.date + 'T00:00:00');
            return apptDate < new Date(todayISO + 'T00:00:00');
          });
          break;
      }
    }
    
    // Time filter
    if (timeFilter !== "All") {
      filtered = filtered.filter(a => {
        if (!a.time) return false;
        const [hours, minutes] = a.time.split(':').map(Number);
        const appointmentTimeInMinutes = (hours || 0) * 60 + (minutes || 0);
        
        switch (timeFilter) {
          case "Morning":
            return appointmentTimeInMinutes >= 6 * 60 && appointmentTimeInMinutes < 12 * 60;
          case "Afternoon":
            return appointmentTimeInMinutes >= 12 * 60 && appointmentTimeInMinutes < 18 * 60;
          case "Evening":
            return appointmentTimeInMinutes >= 18 * 60 && appointmentTimeInMinutes < 22 * 60;
          case "Night":
            return appointmentTimeInMinutes >= 22 * 60 || appointmentTimeInMinutes < 6 * 60;
          default:
            return true;
        }
      });
    }
    
    return filtered;
  }, [appointments, query, selectedDate, dateRangeFilter, timeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  const cancel = async (id) => {
    try {
      await updateAppointmentStatusApi(id, "Cancelled");
      setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, status: "Cancelled" } : a)));
    } catch (e) {
      setError(e.message || "Failed to cancel appointment");
    } finally {
      if (confirmId === id) setConfirmId(null);
    }
  };

  const canCancel = (status) => {
    const normalized = (status || "Pending").toLowerCase();
    return normalized === "pending";
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.container}>
        <div className={styles.toolbar}>
          <h2 className={styles.title}>Appointments</h2>
          <div className={styles.controls}>
            <input
              className={styles.search}
              placeholder="Search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
            <div className={styles.dateFilterContainer}>
              <DatePicker
                selectedDate={selectedDate}
                onDateChange={(date) => {
                  setSelectedDate(date);
                  setDateRangeFilter("All");
                  setPage(1);
                }}
                placeholder="Select specific date"
                dateFormat="yyyy-MM-dd"
                showMonthYearDropdowns={true}
              />
              <select 
                className={styles.filter} 
                value={dateRangeFilter}
                onChange={(e) => {
                  setDateRangeFilter(e.target.value);
                  setSelectedDate(null);
                  setPage(1);
                }}
              >
                <option value="All">All Dates</option>
                <option value="Today">Today</option>
                <option value="Tomorrow">Tomorrow</option>
                <option value="ThisWeek">This Week</option>
                <option value="NextWeek">Next Week</option>
                <option value="ThisMonth">This Month</option>
                <option value="Yesterday">Yesterday</option>
                <option value="LastWeek">Last Week</option>
                <option value="LastMonth">Last Month</option>
                <option value="AllPast">All Past Appointments</option>
              </select>
            </div>
            <select 
              className={styles.filter} 
              value={timeFilter}
              onChange={(e) => {
                setTimeFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="All">All Times</option>
              <option value="Morning">Morning (6AM-12PM)</option>
              <option value="Afternoon">Afternoon (12PM-6PM)</option>
              <option value="Evening">Evening (6PM-10PM)</option>
              <option value="Night">Night (10PM-6AM)</option>
            </select>
          </div>
        </div>

        <div className={styles.grid}>
          {visible.map((a) => (
            <div key={a.id} className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.name}>{a.patient?.name}</div>
                <div className={styles.actions}>
                  <span className={styles.badge}>{a.status || "Pending"}</span>
                  {canCancel(a.status) && (
                    <button className={styles.iconDelete} title="Cancel appointment" onClick={() => setConfirmId(a.id)} aria-label="Cancel appointment">
                      <svg className={styles.trashIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M19 6L18.1 20.2C18.04 21.17 17.23 22 16.26 22H7.74C6.77 22 5.96 21.17 5.9 20.2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M8 6V4.5C8 3.67 8.67 3 9.5 3H14.5C15.33 3 16 3.67 16 4.5V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className={styles.meta}>{formatISOToDDMMYYYY(a.date) || a.date} - {a.time}</div>
            </div>
          ))}
          {visible.length === 0 && (
            <div className={styles.empty}>No appointments found.</div>
          )}
        </div>
        {filtered.length > 0 && (
          <div className={styles.pagination}>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))}>{"<"}</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} className={p === page ? styles.activePage : ""} onClick={() => setPage(p)}>
                {p}
              </button>
            ))}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
          </div>
        )}
        {confirmId && (
          <div className={styles.confirmOverlay} onClick={() => setConfirmId(null)}>
            <div className={styles.confirmContent} onClick={(e) => e.stopPropagation()}>
              <h3 className={styles.confirmTitle}>Cancel Appointment</h3>
              <p>Are you sure you want to cancel this appointment?</p>
              <div className={styles.confirmActions}>
                <Button variant="approve" type="button" onClick={() => setConfirmId(null)}>Cancel</Button>
                <Button variant="cancel" type="button" onClick={() => cancel(confirmId)}>Confirm</Button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

